import { useEffect, useState, useMemo } from "react"
import { useAuth } from "@/context/auth/auth-context"
import { useCurrency } from "@/context/currency/currency-context"
import hive from "@/lib/api/hive"
import { type CalendarEvent, type EarningsEvent } from "@/types/portfolio"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"

type FeedItemType = "payment" | "ex-date" | "earnings"
type EventStatus = "paid" | "declared" | "predicted"
type SectionLabel = "Today" | "Tomorrow" | "This Week" | "Next Week" | "Recent activity"

interface FeedItem {
	id: string
	type: FeedItemType
	ticker: string
	date: string // YYYY-MM-DD
	status?: EventStatus
	amount?: number
	currency?: string
	portfolioName?: string
	earningsTime?: "amc" | "bmo" | "unknown"
	// Earnings detail
	epsEstimate?: number | null
	epsActual?: number | null
	revenueEstimate?: number | null
	revenueActual?: number | null
	isUpcoming?: boolean
	// Dividend
	dividendChange?: "increase" | "decrease" | "same" | ""
	dividendChangePct?: number
	dripProcessed?: boolean
	dripShares?: number
}

interface SectionGroup {
	label: SectionLabel
	items: FeedItem[]
}

function isoToDate(isoString: string): string {
	return isoString.slice(0, 10)
}

function addDays(d: Date, n: number): string {
	return new Date(d.getTime() + n * 86400000).toISOString().slice(0, 10)
}

function sectionFor(dateStr: string, today: Date, todayStr: string, tomorrowStr: string): SectionLabel | null {
	if (dateStr === todayStr) return "Today"
	if (dateStr === tomorrowStr) return "Tomorrow"
	const week7 = addDays(today, 7)
	const week14 = addDays(today, 14)
	if (dateStr > tomorrowStr && dateStr <= week7) return "This Week"
	if (dateStr > week7 && dateStr <= week14) return "Next Week"
	return null
}

function formatCompactRevenue(value: number | null | undefined): string {
	if (value == null) return "—"
	if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`
	if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`
	return `$${value.toLocaleString()}`
}

function beatMissInfo(actual: number | null | undefined, estimate: number | null | undefined) {
	if (actual == null || estimate == null || estimate === 0) return null
	const pct = ((actual - estimate) / Math.abs(estimate)) * 100
	return { pct: Math.abs(pct), beat: pct >= 0 }
}

function earningsTimingShort(time?: string): string {
	if (time === "amc") return "AMC"
	if (time === "bmo") return "BMO"
	return ""
}

function buildSections(calendarEvents: CalendarEvent[], earningsEvents: EarningsEvent[], today: Date): SectionGroup[] {
	const todayStr = today.toISOString().slice(0, 10)
	const tomorrowStr = addDays(today, 1)
	const sevenDaysAgo = addDays(today, -7)

	const items: FeedItem[] = []

	// Payment events
	for (const e of calendarEvents) {
		const payDate = isoToDate(e.payment_date)
		if (!payDate || payDate.startsWith("0001")) continue

		if (e.status === "paid" && payDate >= sevenDaysAgo && payDate < todayStr) {
			items.push({
				id: `pay-${e.ticker}-${e.portfolio_id}-${payDate}`,
				type: "payment",
				ticker: e.ticker,
				date: payDate,
				status: "paid",
				amount: e.amount,
				currency: e.currency,
				portfolioName: e.portfolio_name,
				dividendChange: e.dividend_change,
				dividendChangePct: e.dividend_change_pct,
				dripProcessed: e.drip_processed,
				dripShares: e.drip_shares,
			})
		} else if ((e.status === "declared" || e.status === "predicted") && payDate >= todayStr) {
			const section = sectionFor(payDate, today, todayStr, tomorrowStr)
			if (section) {
				items.push({
					id: `pay-${e.ticker}-${e.portfolio_id}-${payDate}`,
					type: "payment",
					ticker: e.ticker,
					date: payDate,
					status: e.status,
					amount: e.amount,
					currency: e.currency,
					portfolioName: e.portfolio_name,
					dividendChange: e.dividend_change,
					dividendChangePct: e.dividend_change_pct,
					dripProcessed: e.drip_processed,
					dripShares: e.drip_shares,
				})
			}
		}
	}

	// Ex-date events (deduplicated by ticker+date, not portfolio)
	const seenExDates = new Set<string>()
	for (const e of calendarEvents) {
		const exDate = isoToDate(e.ex_date)
		if (!exDate || exDate.startsWith("0001") || exDate < todayStr) continue
		const key = `${e.ticker}-${exDate}`
		if (seenExDates.has(key)) continue
		seenExDates.add(key)

		const section = sectionFor(exDate, today, todayStr, tomorrowStr)
		if (!section) continue

		items.push({
			id: `ex-${key}`,
			type: "ex-date",
			ticker: e.ticker,
			date: exDate,
			amount: e.amount,
			currency: e.currency,
			portfolioName: e.portfolio_name,
			dividendChange: e.dividend_change,
		})
	}

	// Earnings events — past (with actuals) → Recent activity; future → normal sections
	for (const e of earningsEvents) {
		const date = isoToDate(e.date)
		if (!date) continue

		if (date < todayStr && date >= sevenDaysAgo && e.eps_actual != null) {
			// Past earnings with reported actuals go into Recently Paid
			items.push({
				id: `earn-${e.ticker}-${date}`,
				type: "earnings",
				ticker: e.ticker,
				date,
				earningsTime: e.time,
				epsEstimate: e.eps_estimate,
				epsActual: e.eps_actual,
				revenueEstimate: e.revenue_estimate,
				revenueActual: e.revenue_actual,
				isUpcoming: false,
			})
		} else if (date >= todayStr) {
			const section = sectionFor(date, today, todayStr, tomorrowStr)
			if (!section) continue
			items.push({
				id: `earn-${e.ticker}-${date}`,
				type: "earnings",
				ticker: e.ticker,
				date,
				earningsTime: e.time,
				epsEstimate: e.eps_estimate,
				epsActual: e.eps_actual,
				revenueEstimate: e.revenue_estimate,
				revenueActual: e.revenue_actual,
				isUpcoming: e.is_upcoming,
			})
		}
	}

	// Group into sections
	const SECTION_ORDER: SectionLabel[] = ["Today", "Tomorrow", "This Week", "Next Week", "Recent activity"]
	const groupMap = new Map<SectionLabel, FeedItem[]>()

	for (const item of items) {
		const label: SectionLabel | null =
			(item.type === "payment" && item.status === "paid") || (item.type === "earnings" && item.date < todayStr)
				? "Recent activity"
				: sectionFor(item.date, today, todayStr, tomorrowStr)
		if (!label) continue
		if (!groupMap.has(label)) groupMap.set(label, [])
		groupMap.get(label)!.push(item)
	}

	for (const [label, grp] of groupMap.entries()) {
		if (label === "Recent activity") {
			grp.sort((a, b) => b.date.localeCompare(a.date))
		} else {
			grp.sort((a, b) => a.date.localeCompare(b.date))
		}
	}

	return SECTION_ORDER.filter((s) => groupMap.has(s)).map((label) => ({ label, items: groupMap.get(label)! }))
}

function Dot({ type, status }: { type: FeedItemType; status?: EventStatus }) {
	if (type === "earnings") return <span className="mt-1.5 shrink-0 block w-2 h-2 rounded-full bg-indigo-500" />
	if (type === "ex-date")
		return <span className="mt-1.5 shrink-0 block w-2 h-2 rounded-sm border border-dashed border-sky-500" />
	if (status === "paid") return <span className="mt-1.5 shrink-0 block w-2 h-2 rounded-full bg-green-500" />
	return <span className="mt-1.5 shrink-0 block w-2 h-2 rounded-full bg-sky-500" />
}

function FeedRow({
	item,
	format,
	showPortfolio,
}: {
	item: FeedItem
	format: (v: number, opts?: { fromCurrency?: string }) => string
	showPortfolio: boolean
}) {
	const dateLabel = new Date(item.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })

	if (item.type === "earnings") {
		const epsBm = beatMissInfo(item.epsActual, item.epsEstimate)
		const revBm = beatMissInfo(item.revenueActual, item.revenueEstimate)
		const timing = earningsTimingShort(item.earningsTime)
		const subtitle = `Earnings · ${dateLabel}${timing ? ` · ${timing}` : ""}`
		const hasEps = item.epsEstimate != null || item.epsActual != null
		const hasRev = item.revenueEstimate != null || item.revenueActual != null

		const bmBadge = (bm: { pct: number; beat: boolean } | null) =>
			bm ? (
				<span
					className={`text-xs font-medium shrink-0 ${bm.beat ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}
				>
					{bm.beat ? "+" : "-"}
					{bm.pct.toFixed(1)}%
				</span>
			) : null

		return (
			<div className="flex gap-2 items-start">
				<Dot type="earnings" />
				<div className="flex-1 min-w-0">
					<div className="flex items-baseline justify-between gap-1">
						<span className="font-semibold text-sm text-sky-950 dark:text-sky-50 truncate">
							{item.ticker}
						</span>
					</div>
					{hasEps && (
						<div className="flex items-baseline justify-between gap-1">
							<p className="text-xs text-muted-foreground">
								{"EPS"}
								{item.epsEstimate != null && ` est $${item.epsEstimate.toFixed(2)}`}
								{item.epsActual != null && (
									<>
										{" "}
										·{" "}
										<span
											className={
												epsBm
													? epsBm.beat
														? "text-emerald-600 dark:text-emerald-400"
														: "text-red-600 dark:text-red-400"
													: ""
											}
										>
											${item.epsActual.toFixed(2)}
										</span>
									</>
								)}
							</p>
							{bmBadge(epsBm)}
						</div>
					)}
					{hasRev && (
						<div className="flex items-baseline justify-between gap-1">
							<p className="text-xs text-muted-foreground">
								{"Rev"}
								{item.revenueEstimate != null && ` est ${formatCompactRevenue(item.revenueEstimate)}`}
								{item.revenueActual != null && (
									<>
										{" "}
										·{" "}
										<span
											className={
												revBm
													? revBm.beat
														? "text-emerald-600 dark:text-emerald-400"
														: "text-red-600 dark:text-red-400"
													: ""
											}
										>
											{formatCompactRevenue(item.revenueActual)}
										</span>
									</>
								)}
							</p>
							{bmBadge(revBm)}
						</div>
					)}
					<p className="text-xs text-muted-foreground">{subtitle}</p>
				</div>
			</div>
		)
	}

	const subtitle = item.type === "ex-date" ? "Ex-date" : `Payment · ${item.status ?? ""}`

	// E4: dividend change percentage badge
	const showChangePct = item.dividendChangePct != null && item.dividendChangePct !== 0 && item.status !== "predicted"
	const changePctEl =
		showChangePct && item.dividendChangePct != null ? (
			item.dividendChangePct > 0 ? (
				<span className="text-emerald-600 dark:text-emerald-400 text-xs font-medium shrink-0">
					▲ +{item.dividendChangePct.toFixed(1)}%
				</span>
			) : (
				<span className="text-red-600 dark:text-red-400 text-xs font-medium shrink-0">
					▼ {item.dividendChangePct.toFixed(1)}%
				</span>
			)
		) : null

	// E5: DRIP shares display
	const isDrip = item.dripProcessed && item.dripShares != null && item.dripShares > 0

	return (
		<div className="flex gap-2 items-start">
			<Dot type={item.type} status={item.status} />
			<div className="flex-1 min-w-0">
				<div className="flex items-baseline justify-between gap-1">
					<span className="font-semibold text-sm text-sky-950 dark:text-sky-50 truncate">{item.ticker}</span>
					{isDrip && item.dripShares != null ? (
						<span className="text-sm font-medium text-green-500 dark:text-green-400 shrink-0 tabular-nums">
							+{parseFloat(item.dripShares.toFixed(4)).toString()} shares
						</span>
					) : item.amount != null && item.amount > 0 ? (
						<span className="text-sm font-medium text-sky-950 dark:text-sky-50 shrink-0 tabular-nums">
							{format(item.amount, { fromCurrency: item.currency })}
						</span>
					) : null}
				</div>
				<div className="flex items-baseline justify-between gap-1">
					{isDrip && item.amount != null && item.amount > 0 ? (
						<p className="text-xs text-muted-foreground">
							{format(item.amount, { fromCurrency: item.currency })} reinvested · {dateLabel}
						</p>
					) : (
						<p className="text-xs text-muted-foreground">
							{subtitle} · {dateLabel}
						</p>
					)}
					{changePctEl}
				</div>
				{showPortfolio && item.portfolioName && (
					<p className="text-xs text-muted-foreground/60 truncate">{item.portfolioName}</p>
				)}
			</div>
		</div>
	)
}

interface Props {
	portfolioId?: string
}

export function ActivityFeed({ portfolioId }: Props) {
	const { token } = useAuth()
	const { format } = useCurrency()
	const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([])
	const [earningsEvents, setEarningsEvents] = useState<EarningsEvent[]>([])
	const [isLoading, setIsLoading] = useState(true)

	useEffect(() => {
		if (!token) return
		let cancelled = false
		setIsLoading(true)

		const today = new Date()
		const from = addDays(today, -7)
		const to = addDays(today, 14)

		Promise.all([
			hive.fetchCalendarEvents(from, to, token, portfolioId),
			hive.fetchEarningsCalendar(from, to, token, portfolioId),
		])
			.then(([cal, earn]) => {
				if (cancelled) return
				setCalendarEvents(cal)
				setEarningsEvents(earn)
			})
			.catch(() => {
				// Silent — don't break the dashboard
			})
			.finally(() => {
				if (!cancelled) setIsLoading(false)
			})

		return () => {
			cancelled = true
		}
	}, [token, portfolioId])

	const sections = useMemo(
		() => buildSections(calendarEvents, earningsEvents, new Date()),
		[calendarEvents, earningsEvents],
	)

	return (
		<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10">
			<CardHeader className="pb-3">
				<CardTitle className="text-base font-semibold text-sky-950 dark:text-sky-50">Activity</CardTitle>
			</CardHeader>
			<CardContent className="pt-0">
				{isLoading ? (
					<div className="space-y-3">
						{Array.from({ length: 5 }).map((_, i) => (
							<div key={i} className="flex gap-2 items-start">
								<Skeleton className="mt-1.5 w-2 h-2 rounded-full shrink-0" />
								<div className="flex-1 space-y-1.5">
									<Skeleton className="h-3 w-3/4" />
									<Skeleton className="h-2.5 w-1/2" />
								</div>
							</div>
						))}
					</div>
				) : sections.length === 0 ? (
					<p className="text-sm text-muted-foreground text-center py-6">No upcoming events</p>
				) : (
					<div className="space-y-5">
						{sections.map((section) => (
							<div key={section.label}>
								<p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
									{section.label}
								</p>
								<div className="space-y-2.5">
									{section.items.map((item) => (
										<FeedRow
											key={item.id}
											item={item}
											format={format}
											showPortfolio={!portfolioId}
										/>
									))}
								</div>
							</div>
						))}
					</div>
				)}
			</CardContent>
		</Card>
	)
}
